import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../components/Toast/toast.ts";
import moment from "moment";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Store,
  FileText,
  MessageSquare,
  ImageIcon,
  Video,
  Copy,
  Share2,
  Eye,
  Send,
  Tag,
  Hash,
  Camera,
  ClipboardList,
} from "lucide-react";
import { RWebShare } from "react-web-share";
import CustomVideoPlayer from "../../../src/components/CustomVideoPlayer/CustomVideoPlayerForDetailsPage.tsx";
import CustomImage from "../../../src/components/CustomImg/CustomImage.tsx";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import { StatusChip } from "../../components/StatusChip/index.ts";

/* ─── Reusable Section Card ─── */
const SectionCard = ({
  title,
  icon: Icon,
  children,
  headerRight,
  className = "",
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden ${className}`}
  >
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-2)/0.5)]">
      <div className="w-6 h-6 rounded-md bg-gold-2/10 flex items-center justify-center">
        <Icon size={12} className="text-gold-2" />
      </div>
      <span className="text-[13px] font-semibold text-[rgb(var(--text-primary))]">
        {title}
      </span>
      {headerRight && <div className="ml-auto">{headerRight}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ─── Info Row ─── */
const InfoRow = ({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: any;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-[rgb(var(--border-primary))] last:border-b-0">
    <div className="w-7 h-7 rounded-md bg-[rgb(var(--surface-3))] flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={13} className="text-gold-2" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-0.5">
        {label}
      </p>
      {children ?? (
        <p className="text-[13px] text-[rgb(var(--text-primary))] font-medium leading-snug">
          {value || "—"}
        </p>
      )}
    </div>
  </div>
);

/* ─── Skeleton ─── */
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`rounded-md bg-[rgb(var(--surface-3))] animate-pulse ${className}`} />
);

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-1 flex flex-col gap-4">
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5 flex flex-col gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <Skeleton className="w-7 h-7 shrink-0" />
            <div className="flex-1">
              <Skeleton className="h-2.5 w-16 mb-1.5" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="lg:col-span-2 flex flex-col gap-4">
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5">
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-28 w-full" />
      </div>
      <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5">
        <Skeleton className="h-4 w-32 mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const EvidenceRequestPage: React.FC = () => {
  const { evidenceRequestId } = useParams();
  const navigate = useNavigate();
  const storedToken = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [evidenceRequestDetails, setEvidenceRequestDetails] = useState<any>(null);
  const [reload, setReload] = useState(false);
  const [commentsVal, setCommentsVal] = useState("");

  /* Media lightbox */
  const [imgHeader, setImgHeader] = useState("");
  const [imgVisible, setImgVisible] = useState(false);
  const [imgSrc, setImgSrc] = useState("");

  /* Formatters */
  const formatCommentDate = (d: string) =>
    moment(d).format("MMM DD, YYYY · hh:mm A");
  const formatEvidenceDate = (d: string) => {
    if (!d) return "—";
    const idx = d.indexOf("T");
    return idx !== -1
      ? moment(d.substring(0, idx)).format("MMM DD, YYYY")
      : moment(d).format("MMM DD, YYYY");
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("URL copied to clipboard"))
      .catch((e) => toast.error(e?.message));
  };

  /* Fetch */
  const getEvidenceRequestDetails = async () => {
    if (!storedToken) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/evidence-request/${evidenceRequestId}`,
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      setEvidenceRequestDetails(response?.data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEvidenceRequestDetails();
    // eslint-disable-next-line
  }, [reload]);

  /* Add comment */
  const handleAddComment = async () => {
    if (!commentsVal.trim()) return;
    try {
      setSubmittingComment(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/evidence-request/comment`,
        { id: evidenceRequestId, payload: commentsVal },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      toast.success("Your comment added successfully!");
      setCommentsVal("");
      setReload((r) => !r);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong while adding comment!"
      );
    } finally {
      setSubmittingComment(false);
    }
  };

  /* Derived */
  const { evidence_date, evidence_details, evidence_subject, store_name, evidence_status } =
    evidenceRequestDetails || {};

  const validMedia = (evidenceRequestDetails?.media || []).filter(Boolean);
  const videos = validMedia.filter((m: any) => m?.file_url?.endsWith(".mp4"));
  const images = validMedia.filter((m: any) => !m?.file_url?.endsWith(".mp4"));

  /* Status badge variant */
  const statusVariant =
    evidence_status === "Completed" || evidence_status === "Sent"
      ? "success"
      : evidence_status === "Pending"
      ? "danger"
      : "neutral";
  const statusLabel =
    evidence_status === "Pending"
      ? "Requested"
      : evidence_status === "Completed" || evidence_status === "Sent"
      ? "Resolved"
      : evidence_status || "—";

  /* ── render ── */
  return (
    <div className="flex flex-col gap-6">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg border border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-1))] hover:bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
        >
          <ArrowLeft size={15} />
        </button>
        <DescriptionBox
          title="Client Queries"
          subTitle="Evidence Request Detail"
          description="Full detail view for this evidence request query."
        />
        {evidence_status && !loading && (
          <div className="ml-auto">
            <StatusChip label={statusLabel} variant={statusVariant} />
          </div>
        )}
      </div>

      {/* ── Image Dialog ── */}
      <Dialog
        header={imgHeader}
        visible={imgVisible}
        maximized
        style={{ width: "100vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={() => setImgVisible(false)}
      >
        <div className="flex items-center justify-center w-full h-full">
          <CustomImage src={imgSrc} alt={imgHeader} imageName={imgHeader} />
        </div>
      </Dialog>

      {/* ── Body ── */}
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-1 flex flex-col gap-4">

            {/* Core Info */}
            <SectionCard title="Request Info" icon={ClipboardList}>
              <InfoRow
                icon={Hash}
                label="Request ID"
                value={evidenceRequestId?.toString().slice(-8).toUpperCase()}
              />
              <InfoRow icon={Calendar} label="Evidence Date">
                <p className="text-[13px] font-medium text-[rgb(var(--text-primary))]">
                  {formatEvidenceDate(evidence_date)}
                </p>
              </InfoRow>
              <InfoRow icon={Store} label="Store" value={store_name} />
              <InfoRow icon={Tag} label="Subject" value={evidence_subject} />
            </SectionCard>

            {/* Status Card */}
            {evidence_status && (
              <SectionCard title="Request Status" icon={FileText}>
                <div
                  className={`flex items-center gap-3 p-4 rounded-lg border ${
                    statusVariant === "success"
                      ? "bg-[rgb(var(--success)/0.08)] border-[rgb(var(--success)/0.25)]"
                      : statusVariant === "danger"
                      ? "bg-[rgb(var(--danger)/0.08)] border-[rgb(var(--danger)/0.25)]"
                      : "bg-[rgb(var(--surface-2))] border-[rgb(var(--border-primary))]"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 animate-pulse ${
                      statusVariant === "success"
                        ? "bg-[rgb(var(--success))]"
                        : statusVariant === "danger"
                        ? "bg-[rgb(var(--danger))]"
                        : "bg-[rgb(var(--text-muted))]"
                    }`}
                  />
                  <div>
                    <p
                      className={`text-[14px] font-bold ${
                        statusVariant === "success"
                          ? "text-[rgb(var(--success))]"
                          : statusVariant === "danger"
                          ? "text-[rgb(var(--danger))]"
                          : "text-[rgb(var(--text-secondary))]"
                      }`}
                    >
                      {statusLabel}
                    </p>
                    <p className="text-[11px] text-[rgb(var(--text-muted))] mt-0.5">
                      Current request status
                    </p>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Evidence Details */}
            <SectionCard title="Evidence Details" icon={FileText}>
              <p className="text-[13px] text-[rgb(var(--text-secondary))] leading-relaxed whitespace-pre-wrap">
                {evidence_details || "No details provided."}
              </p>
            </SectionCard>

            {/* Media */}
            <SectionCard
              title="Media Evidence"
              icon={Camera}
              headerRight={
                <span className="text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
                  {validMedia.length} file{validMedia.length !== 1 ? "s" : ""}
                </span>
              }
            >
              {validMedia.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                  <ImageIcon
                    size={28}
                    className="text-[rgb(var(--text-muted))] opacity-30"
                  />
                  <p className="text-[12px] text-[rgb(var(--text-muted))]">
                    No media attached
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {/* Videos */}
                  {videos.map((media: any, i: number) => (
                    <div
                      key={i}
                      className="max-w-[400px] rounded-lg border border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-2))] overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[rgb(var(--border-primary))]">
                        <div className="flex items-center gap-2">
                          <Video size={13} className="text-gold-2" />
                          <span className="text-[12px] font-medium text-[rgb(var(--text-primary))] truncate max-w-[220px]">
                            {media?.file_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyToClipboard(media?.file_url)}
                            title="Copy URL"
                            className="p-1.5 rounded-md hover:bg-[rgb(var(--surface-3))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                          >
                            <Copy size={13} />
                          </button>
                          <RWebShare
                            data={{
                              text: `Check out this media: ${media?.file_name}`,
                              url: media?.file_url,
                              title: "Share Media",
                            }}
                          >
                            <button
                              title="Share"
                              className="p-1.5 rounded-md hover:bg-[rgb(var(--surface-3))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                            >
                              <Share2 size={13} />
                            </button>
                          </RWebShare>
                        </div>
                      </div>
                      <div className="p-3">
                        <CustomVideoPlayer
                          src={media?.file_url}
                          videoName={media?.file_name}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Images */}
                  {images.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2.5">
                        Images ({images.length})
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {images.map((media: any, i: number) => (
                          <motion.div
                            key={i}
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.15 }}
                            className="relative rounded-lg overflow-hidden border border-[rgb(var(--border-primary))] cursor-pointer group aspect-video bg-[rgb(var(--surface-3))]"
                            onClick={() => {
                              setImgSrc(media?.file_url);
                              setImgHeader(media?.file_name);
                              setImgVisible(true);
                            }}
                          >
                            <img
                              src={media?.file_url}
                              alt={media?.file_name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <Eye size={18} className="text-white" />
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-[10px] text-white truncate">
                                {media?.file_name}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Comments */}
            <SectionCard
              title="Comments History"
              icon={MessageSquare}
              headerRight={
                <span className="text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
                  {evidenceRequestDetails?.comments?.length || 0}
                </span>
              }
            >
              <div className="flex flex-col gap-3">
                {/* Comment list */}
                {evidenceRequestDetails?.comments?.length > 0 ? (
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1">
                    <AnimatePresence>
                      {evidenceRequestDetails.comments.map(
                        (item: any, index: number) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              delay: index * 0.04,
                              duration: 0.25,
                            }}
                            className="flex gap-3 p-3 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]"
                          >
                            <div className="w-7 h-7 rounded-full bg-gold-2/15 flex items-center justify-center shrink-0 text-[11px] font-bold text-gold-2">
                              {item?.comment_by?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[12px] font-semibold text-[rgb(var(--text-primary))]">
                                  {item?.comment_by || "Unknown"}
                                </span>
                                <span className="text-[10px] text-[rgb(var(--text-muted))]">
                                  {formatCommentDate(item?.comment_date)}
                                </span>
                              </div>
                              <p className="text-[12px] text-[rgb(var(--text-secondary))] leading-relaxed">
                                {item?.comment}
                              </p>
                            </div>
                          </motion.div>
                        )
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <MessageSquare
                      size={24}
                      className="text-[rgb(var(--text-muted))] opacity-30"
                    />
                    <p className="text-[12px] text-[rgb(var(--text-muted))]">
                      No comments yet
                    </p>
                  </div>
                )}

                {/* Add comment */}
                <div className="border-t border-[rgb(var(--border-primary))] pt-4 mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2">
                    Add Comment
                  </p>
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={commentsVal}
                      onChange={(e) => setCommentsVal(e.target.value)}
                      placeholder="Type your comment here..."
                      rows={3}
                      className="w-full px-3 py-2.5 text-[13px] rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddComment}
                        disabled={!commentsVal.trim() || submittingComment}
                        style={{background: "var(--gold-grad)"}}
                        className="flex items-center gap-2 px-4 h-[38px] rounded-md hover:bg-gold-3 text-white text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? (
                          <ProgressSpinner
                            style={{ width: "16px", height: "16px" }}
                            strokeWidth="3"
                            animationDuration=".5s"
                          />
                        ) : (
                          <Send size={13} />
                        )}
                        Submit Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EvidenceRequestPage;